import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import AuditCard from './components/AuditCard';
import CreateAuditModal from './components/CreateAuditModal';
import AuditExecutionModal from './components/AuditExecutionModal';
import AuditDeleteConfirmModal from './components/AuditDeleteConfirmModal';
import { auditService } from '../../services/auditService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import useSiteReadOnly from '../../hooks/useSiteReadOnly';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import { toast } from 'sonner';

const AuditsInspections = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const [searchParams] = useSearchParams();
  const filterSiteId = searchParams?.get('siteId');

  const [audits, setAudits] = useState([]);
  const [sites, setSites] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSite, setSelectedSite] = useState('all');
  // Only gate when a specific site is selected (not "all").
  const gatedSiteId = selectedSite && selectedSite !== 'all' ? selectedSite : null;
  const { status: gatedSiteStatus, isReadOnly: gatedSiteReadOnly } = useSiteReadOnly(gatedSiteId);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isExecutionModalOpen, setIsExecutionModalOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);

  // NSherjFh PR 7: tab between active and soft-deleted audits.
  // Admin-only. RLS would permit site_managers to soft-delete via
  // admins_sm_update_site_audits, but the UI exposes Delete/Restore
  // to admins only — same pattern as PRs 4-6 (RLS may permit more
  // than the UI exposes).
  const isAdmin = userProfile?.role === 'admin';
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingAudit, setDeletingAudit] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const breadcrumbItems = [
    { label: t('home'), href: '/admin-dashboard' },
    { label: t('auditsInspections') }
  ];

  const handleBack = () => {
    if (window.history?.length > 1) {
      navigate(-1);
    } else {
      navigate('/admin-dashboard');
    }
  };

  useEffect(() => {
    loadData();
  }, [viewDeleted]);

  useEffect(() => {
    if (filterSiteId && sites?.length > 0) {
      setSelectedSite(filterSiteId);
    }
  }, [filterSiteId, sites]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [companyResult, sitesResult, auditsResult] = await Promise.all([
        companyService?.getUserCompany(),
        siteService?.getAllSites(),
        auditService?.getAllAudits({ includeDeleted: viewDeleted })
      ]);

      if (companyResult?.error) throw companyResult.error;
      if (sitesResult?.error) throw sitesResult.error;
      if (auditsResult?.error) throw auditsResult.error;

      setCompanyId(companyResult?.data?.id);
      setSites(sitesResult?.data);
      // NSherjFh PR 7: deleted tab shows ONLY soft-deleted audits.
      // includeDeleted: true returns BOTH active and deleted; narrow
      // to the deleted ones for display.
      const rows = auditsResult?.data || [];
      setAudits(viewDeleted ? rows?.filter((a) => a?.deleted_at) : rows);
    } catch (err) {
      console.error('[AuditsInspections] load inspections failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading inspections: ${err?.message}`);
      } else {
        setError(`Failed to load inspections: ${err?.message || 'Unknown error'}. Please refresh the page.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredAudits = audits?.filter(audit => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery?.toLowerCase();
      const matchesSearch = 
        audit?.audit_name?.toLowerCase()?.includes(searchLower) ||
        audit?.template_type?.toLowerCase()?.includes(searchLower) ||
        audit?.sites?.site_name?.toLowerCase()?.includes(searchLower) ||
        audit?.user_profiles?.full_name?.toLowerCase()?.includes(searchLower);
      if (!matchesSearch) return false;
    }

    if (selectedSite !== 'all' && audit?.site_id !== selectedSite) return false;
    if (selectedStatus !== 'all' && audit?.status !== selectedStatus) return false;
    return true;
  });

  const handleCreateAudit = async (auditData) => {
    // Inline site-status check using the same hook's helper. The audit
    // form picks its own siteId so we can't use the page-level gatedSite
    // hook here — we'd need to refetch synchronously. Defer to the DB
    // trigger and surface its message via the existing error flow if it
    // fires. The page-level "newAudit" button is already gated for the
    // filter-selected site case.
    const result = await auditService?.createAudit({
      ...auditData,
      companyId
    });

    if (result?.error) {
      const err = result?.error;
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied creating inspection: ${err?.message}`);
      } else {
        setError(`Failed to create inspection: ${err?.message || 'Unknown error'}`);
      }
    } else {
      setIsCreateModalOpen(false);
      loadData();
    }
  };

  const handleViewAudit = (audit) => {
    setSelectedAudit(audit);
    setIsExecutionModalOpen(true);
  };

  // NSherjFh PR 7 — soft-delete handlers. Mirrors PRs 3-6 flow.
  const handleDeleteClick = (audit) => {
    setDeletingAudit(audit);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAudit?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingAudit;
    try {
      const result = await auditService?.deleteAudit(target?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Delete failed');
      setAudits((prev) => prev?.filter((a) => a?.id !== target?.id));
      toast?.success('Audit moved to Deleted');
    } catch (err) {
      console.error('[AuditsInspections] delete failed:', err);
      toast?.error(err?.message || 'Failed to delete audit');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingAudit(null);
    }
  };

  const handleRestore = async (audit) => {
    try {
      const result = await auditService?.restoreAudit(audit?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Restore failed');
      setAudits((prev) => prev?.filter((a) => a?.id !== audit?.id));
      toast?.success('Audit restored');
    } catch (err) {
      console.error('[AuditsInspections] restore failed:', err);
      toast?.error(err?.message || 'Failed to restore audit');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedSite('all');
    setSelectedStatus('all');
  };

  const hasActiveFilters = searchQuery || selectedSite !== 'all' || selectedStatus !== 'all';

  const siteOptions = [
    { value: 'all', label: 'All Sites' },
    ...sites?.map(site => ({
      value: site?.id,
      label: site?.site_name
    }))
  ];

  const statusOptions = [
    { value: 'all', label: t('allStatuses') },
    { value: 'draft', label: t('draft') },
    { value: 'in_progress', label: t('inProgress') },
    { value: 'completed', label: t('completed') }
  ];

  const getStatusCounts = () => {
    return {
      total: audits?.length || 0,
      draft: audits?.filter(a => a?.status === 'draft')?.length || 0,
      inProgress: audits?.filter(a => a?.status === 'in_progress')?.length || 0,
      completed: audits?.filter(a => a?.status === 'completed')?.length || 0
    };
  };

  const counts = getStatusCounts();

  return (
    <>
      <Helmet>
        <title>Audits & Inspections - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <div className="mb-6 flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowLeft"
              onClick={handleBack}
            >
              {t('back')}
            </Button>
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <SiteReadOnlyBanner status={gatedSiteStatus} siteId={gatedSiteId} className="mb-6" />

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {viewDeleted ? 'Deleted audits' : t('auditsInspections')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {viewDeleted
                  ? 'Audits removed from active views. Restore to bring them back.'
                  : t('auditsInspectionsSubtitle')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* NSherjFh PR 7: admin-only View Deleted toggle. */}
              {isAdmin && (
                <Button
                  variant={viewDeleted ? 'default' : 'outline'}
                  iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
                  onClick={() => setViewDeleted((v) => !v)}
                >
                  {viewDeleted ? 'View active' : 'View deleted'}
                </Button>
              )}
              {!viewDeleted && userProfile?.role === 'admin' && (
                <Button
                  variant="default"
                  iconName="Plus"
                  onClick={() => setIsCreateModalOpen(true)}
                  disabled={subscriptionReadOnly || gatedSiteReadOnly}
                >
                  {t('newAudit')}
                </Button>
              )}
            </div>
          </div>

          {/* NSherjFh PR 7: stats + filters are meaningful only on the
              active tab. Hide them when viewing soft-deleted audits. */}
          {!viewDeleted && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('totalAudits')}</p>
                  <p className="text-2xl font-bold text-foreground">{counts?.total}</p>
                </div>
                <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg">
                  <Icon name="ClipboardCheck" size={24} color="var(--color-accent)" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('draft')}</p>
                  <p className="text-2xl font-bold text-foreground">{counts?.draft}</p>
                </div>
                <div className="flex items-center justify-center w-12 h-12 bg-muted rounded-lg">
                  <Icon name="FileEdit" size={24} color="var(--color-muted-foreground)" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('inProgress')}</p>
                  <p className="text-2xl font-bold text-foreground">{counts?.inProgress}</p>
                </div>
                <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg">
                  <Icon name="Clock" size={24} color="var(--color-warning)" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('completed')}</p>
                  <p className="text-2xl font-bold text-foreground">{counts?.completed}</p>
                </div>
                <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg">
                  <Icon name="CheckCircle" size={24} color="var(--color-success)" />
                </div>
              </div>
            </div>
          </div>
          )}

          {!viewDeleted && (
          <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-2 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">{t('filterAudits')}</h3>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1"
                >
                  <Icon name="X" size={14} />
                  {t('clearFilters')}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('searchAuditsPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10"
                />
              </div>
              <Select
                label={t('filterBySite')}
                options={siteOptions}
                value={selectedSite}
                onChange={setSelectedSite}
              />
              <Select
                label={t('filterByStatus')}
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
              />
            </div>
          </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            </div>
          ) : error ? (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 text-center">
              <Icon name="AlertCircle" size={24} color="var(--color-error)" className="mx-auto mb-2" />
              <p className="text-error font-medium">{error}</p>
            </div>
          ) : filteredAudits?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto mb-4">
                <Icon name="ClipboardCheck" size={32} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {hasActiveFilters ? t('noMatchingAudits') : t('noAuditsFound')}
              </h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters
                  ? t('tryAdjustingFilters')
                  : t('getStartedCreateAudit')}
              </p>
              {userProfile?.role === 'admin' && (
                <Button
                  variant="default"
                  iconName="Plus"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  {t('createFirstAudit')}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAudits?.map(audit => (
                <AuditCard
                  key={audit?.id}
                  audit={audit}
                  siteName={audit?.sites?.site_name || ''}
                  companyName={audit?.companies?.company_name || ''}
                  onView={() => handleViewAudit(audit)}
                  onDelete={!viewDeleted && isAdmin ? handleDeleteClick : null}
                  onRestore={viewDeleted && isAdmin ? handleRestore : null}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {isCreateModalOpen && (
        <CreateAuditModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreateAudit}
          sites={sites}
        />
      )}

      {isExecutionModalOpen && selectedAudit && (
        <AuditExecutionModal
          isOpen={isExecutionModalOpen}
          onClose={() => {
            setIsExecutionModalOpen(false);
            setSelectedAudit(null);
            loadData();
          }}
          audit={selectedAudit}
        />
      )}

      <AuditDeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeletingAudit(null); }}
        onConfirm={handleConfirmDelete}
        auditLabel={deletingAudit?.audit_name || ''}
      />
    </>
  );
};

export default AuditsInspections;